import { useEffect, useState } from 'react';
import { useUser } from '../../Data/UserContext';
import { trackProductSelected } from '../../utils/frontendTrackingEvents';
import CustomModal from '../Modals';
import PaymentModal from './PaymentModal';
import SaleCountdown from './SaleCountdown';
import { bestValueOfferId, currentPrice, getHardOffers, OffersTypes } from './api/offers';

const SHOP_PAGE_PATH = '/shop';

/** Skeleton card matching one "Get rubies" offer (icon, amount, button) */
function HardOfferCardSkeleton() {
  return (
    <div className="flex flex-col justify-center items-center gap-1 relative px-2 py-3 bg-white w-full min-w-0">
      <div className="w-10 h-10 bg-gray-200 animate-pulse shrink-0" aria-hidden />
      <div className="h-5 w-14 bg-gray-200 animate-pulse shrink-0" aria-hidden />
      <div className="h-10 w-[100px] bg-gray-200 animate-pulse shrink-0" aria-hidden />
    </div>
  );
}

function HardOffersSkeleton() {
  return (
    <div className="flex w-[100%] items-center justify-evenly gap-0.5 overflow-x-auto overflow-y-hidden bg-inherit px-[3px]">
      {[1, 2, 3].map((i) => (
        <HardOfferCardSkeleton key={i} />
      ))}
    </div>
  );
}

interface HardOffersProps {
  updateRubies: (rubies: number) => void;
  setIsAnimatingRubies?: (isAnimating: boolean) => void;
  onOffersLoaded?: (hasOffers: boolean) => void;
}

export default function HardOffers({ updateRubies, setIsAnimatingRubies, onOffersLoaded }: HardOffersProps) {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [hardOffers, setHardOffers] = useState<OffersTypes[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<OffersTypes | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchHardOffers();
  }, []);

  async function fetchHardOffers({ silent = false }: { silent?: boolean } = {}) {
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const hardData = await getHardOffers();
      setHardOffers(hardData);
      onOffersLoaded?.(hardData.length > 0);
    } catch (error) {
      console.error(error);
      if (!silent) {
        setError(error instanceof Error ? error.message : String(error));
        onOffersLoaded?.(false);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }

  const refreshAfterSaleEnds = () => void fetchHardOffers({ silent: true });

  function openOfferModal(item: OffersTypes) {
    setSelectedOffer(item);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
    setSelectedOffer(null);
    void fetchHardOffers({ silent: true });
  }

  if (loading) return <HardOffersSkeleton />;
  if (error)
    return (
      <div className="flex flex-col items-center gap-2 py-3" role="alert">
        <p className="font-anton text-white">Couldn't load ruby packs.</p>
        <button
          type="button"
          onClick={() => void fetchHardOffers()}
          className="bg-lime px-6 py-2 font-anton text-main"
        >
          Retry
        </button>
      </div>
    );
  if (hardOffers.length === 0) return null;

  // Hide BEST VALUE while any pack is on sale — sale pricing can change which pack is cheapest.
  const anyOnSale = hardOffers.some((o) => o.on_sale && o.sale_percent);
  const bestValueId = anyOnSale ? null : bestValueOfferId(hardOffers);

  return (
    <>
      <div className="flex w-[100%] items-stretch justify-evenly gap-0.5 overflow-x-auto overflow-y-hidden bg-inherit px-[3px]">
        {hardOffers.map((item: OffersTypes) => {
          const price = currentPrice(item);
          const onSale = Boolean(item.on_sale && item.sale_percent);
          return (
            <div
              key={item.id}
              className={`flex flex-col justify-center items-center gap-1 relative overflow-hidden px-2 py-3 bg-white w-full ${
                onSale ? 'ring-2 ring-inset ring-red' : ''
              }`}
            >
              {onSale ? (
                <div className="absolute p-[2px] text-[13px] w-[120px] bg-red top-[15px] right-[-40px] rotate-[45deg] text-center font-anton text-white">
                  −{item.sale_percent}%
                </div>
              ) : item.id === bestValueId ? (
                <div className="absolute p-[2px] text-[10px] w-[120px] bg-goldB top-[15px] right-[-40px] rotate-[45deg] text-center font-anton text-main">
                  BEST VALUE
                </div>
              ) : null}
              <img
                className='w-[40px] h-[40px]'
                src='/assets/currency-ruby.png'
                alt='ruby'
              />
              <p className='text-red font-anton font-[400] text-[20px]'>
                x{item.receive_amount}
              </p>
              {onSale ? (
                <p className="flex items-center gap-[4px] font-anton text-[13px] leading-none">
                  <span className="text-gray-400 line-through" aria-label={`Was ₹${item.pay_amount}`}>
                    ₹{item.pay_amount}
                  </span>
                  <span className="bg-red px-[4px] py-[1px] text-[11px] uppercase text-white">
                    Save ₹{item.pay_amount - price}
                  </span>
                </p>
              ) : null}
              <button
                onClick={() => {
                  trackProductSelected({
                    user_id: user?.userId,
                    product_id: item.id,
                    product_type: 'ruby_pack',
                    product_name: item.offer_name?.trim() || `x${item.receive_amount}_rubies`,
                    price,
                    ...(onSale && { original_price: item.pay_amount, discount_pct: item.sale_percent }),
                    currency: 'INR',
                    pathname: SHOP_PAGE_PATH,
                  });
                  openOfferModal(item);
                }}
                className="font-anton text-white uppercase font-[400] text-[14px] flex flex-row items-center justify-center gap-[2px] bg-lime whitespace-nowrap w-[100px] h-[40px] text-center font-[400]"
              >
                <span className="font-anton font-[14px] text-[#1B062F]">GET FOR ₹{price}</span>
              </button>
              {onSale && item.sale_ends_at ? (
                <SaleCountdown endsAt={item.sale_ends_at} onExpire={refreshAfterSaleEnds} className="text-red" />
              ) : null}
            </div>
          );
        })}
      </div>
      <CustomModal
        isOpen={open}
        onRequestClose={closeModal}
        contentLabel='BUY'
        contentClassName="hide-scrollbar"
        children={
          selectedOffer && (
            <PaymentModal
              receiveAmount={selectedOffer.receive_amount}
              payAmount={currentPrice(selectedOffer)}
              originalPayAmount={selectedOffer.on_sale ? selectedOffer.pay_amount : undefined}
              saleEndsAt={selectedOffer.on_sale ? selectedOffer.sale_ends_at : null}
              offerId={selectedOffer.id}
              updateRubies={updateRubies}
              closeModal={closeModal}
              offerType={selectedOffer.offer_type}
              setIsAnimatingRubies={setIsAnimatingRubies}
            />
          )
        }
      />
    </>
  );
}
